import _ from 'underscore';
import React from 'react';
import { render as reactRender, unmountComponentAtNode } from 'react-dom';
import { batchActions } from 'redux-batched-actions';
import { Provider } from 'react-redux';
import '../stylesheets/main.less';
import pick from 'lodash.pick';

import { store } from './store';

import * as authActions from './actions/auth';
import * as userActions from './actions/user';
import { updateText, setLocale, setConnectNotificationAllowed } from './actions/ui';
import { setCurrentLocation } from './actions/browser';
import * as conversationActions from './actions/conversation';
import * as appStateActions from './actions/app-state';
import { setConfig, fetchConfig, setLocalSettings } from './actions/config';
import { reset } from './actions/common';
import { reconnectFaye, disconnectFaye } from './actions/faye';
import * as typingActions from './actions/typing';

import { observable, observeStore } from './utils/events';
import { delegate } from './utils/delegate';
import { waitForPage, monitorBrowserState, stopMonitoringBrowserState, updateHostClassNames } from './utils/dom';
import { isFileUploadSupported } from './utils/media';
import { isAudioSupported, throttleNotificationSound } from './utils/sound';
import * as storage from './utils/storage';
import { getSizeValue } from './utils/dimensions';
import { formatConversationOnGet, isValidConversationId } from './utils/conversation';
import { filterClientProps, isCurrentUserMessage } from './utils/message';
import * as logger from './utils/logger';
import * as rollbarLib from './utils/rollbar';

import { WIDGET_STATE } from './constants/app';
import { DEFAULT_LOCALE } from './constants/i18n';
import { DISPLAY_STYLE } from './constants/styles';
import { MESSENGER_BUTTON } from './constants/dimensions';

import { IFRAME_ID } from '../../shared/js/constants/ids';

import Widget from './components/Widget';
import { CONNECTION_STATE } from './constants/connection-state';
import { CLIENT_ID_ERROR } from './constants/client';

let lastTriggeredMessageTimestamp = 0;
let initialStoreChange = true;
let unsubscribeFromStore;

window.addEventListener('offline', () => {
    logger.debug('window:offline');
    store.dispatch(appStateActions.setConnectionState(CONNECTION_STATE.DISCONNECTED_WITH_RETRY));
});

window.addEventListener('online', () => {
    const state = store.getState();

    logger.debug('window:offline');

    if (state.user._id) {
        reconnectFaye();
    } else {
        store.dispatch(appStateActions.setConnectionState(CONNECTION_STATE.CONNECTED));
    }
});

// Add ie11 class to body for ie11 specific css
if (!!window.MSInputMethodContext && !!document.documentMode) {
    document.body.classList.add('ie11');
}

// Listen for media query changes from the host page.
window.addEventListener(
    'message',
    ({ data, origin }) => {
        if (origin === `${parent.document.location.protocol}//${parent.document.location.host}`) {
            if (data.type === 'sizeChange') {
                store.dispatch(appStateActions.updateWidgetSize(data.value));
            }
        }
    },
    false,
);

function adjustPageSize() {
    const {
        config: { displayStyle, buttonWidth, buttonHeight },
    } = store.getState();

    if (displayStyle === DISPLAY_STYLE.BUTTON) {
        const contentWidth = getSizeValue(buttonWidth) || MESSENGER_BUTTON.DEFAULT_WIDTH;
        const contentHeight = getSizeValue(buttonHeight) || MESSENGER_BUTTON.DEFAULT_HEIGHT;

        const iframe = parent.document.querySelector(`iframe#${IFRAME_ID}`);

        iframe.width = contentWidth + MESSENGER_BUTTON.SHADOW_WIDTH * 2;
        iframe.height = contentHeight + MESSENGER_BUTTON.VERTICAL_SPACING + MESSENGER_BUTTON.SHADOW_WIDTH * 2;
    }
}

function renderWidget() {
    waitForPage().then(() => {
        adjustPageSize();
        const mount = document.querySelector('#mount');
        reactRender(
            <Provider store={store}>
                <Widget />
            </Provider>,
            mount,
        );
    });
}

observable.on('message:sent', (message, data) => {
    observable.trigger('message', message, data);
});
observable.on('message:received', (message, data) => {
    observable.trigger('message', message, data);
});

function handleNotificationSound() {
    const {
        config: { soundNotificationEnabled },
    } = store.getState();

    if (soundNotificationEnabled) {
        throttleNotificationSound();
    }
}

function onStoreChange(props, previousProps) {
    const {
        conversation: { messages, replyActions, unreadCount, _id: conversationId },
        widgetState,
        displayStyle,
        isInitialized,
        typingEvents,
        currentUserId,
    } = props;
    const {
        conversation: {
            messages: previousMessages,
            replyActions: previousReplyActions,
            unreadCount: previousUnreadCount,
            _id: previousConversationId,
        },
        widgetState: previousWidgetState,
        displayStyle: previousDisplayStyle,
        typingEvents: previousTypingEvents,
    } = previousProps;

    const hasReplyActions = replyActions && replyActions.actions && replyActions.actions.length > 0;
    const replyActionsChanged =
        hasReplyActions &&
        (replyActions.actions.length !== previousReplyActions.actions.length ||
            previousReplyActions.actions[0]._id !== replyActions.actions[0]._id);
    const hasTextlessMessageWithReplyActions =
        hasReplyActions && replyActions.message && (!replyActions.message.text || !replyActions.message.text.trim());

    let unreadMessageCount = unreadCount;
    let triggerSound = false;

    if (conversationId && conversationId !== previousConversationId) {
        return;
    }

    // If a message has no text, it won't appear in the messages collection so we need to trigger this manually
    if (replyActionsChanged && hasTextlessMessageWithReplyActions) {
        // Bookboost: Disable sound for initial actions:
        // triggerSound = true;

        if (isInitialized) {
            observable.trigger('message:received', filterClientProps(replyActions.message), {
                conversation: { _id: conversationId },
            });
        }

        unreadMessageCount = unreadCount - 1;
    }

    if (messages.length > 0) {
        const nonCurrentUserMessages = messages.filter((message) => !isCurrentUserMessage(message, currentUserId));
        const previousNonCurrentUserMessages = previousMessages.filter(
            (message) => !isCurrentUserMessage(message, currentUserId),
        );
        const newMessageCount = nonCurrentUserMessages.length - previousNonCurrentUserMessages.length;

        newMessageCount &&
            nonCurrentUserMessages
                .slice(-newMessageCount)
                .filter((message) => message.received > lastTriggeredMessageTimestamp)
                .forEach((message) => {
                    if (isInitialized) {
                        observable.trigger('message:received', filterClientProps(message), {
                            conversation: { _id: conversationId },
                        });
                    }

                    lastTriggeredMessageTimestamp = message.received;

                    if (!initialStoreChange) {
                        triggerSound = true;
                    }
                });
    }

    const observablePayload = { conversation: { _id: conversationId } };

    if (unreadMessageCount >= 0 && unreadMessageCount !== previousUnreadCount) {
        observable.trigger('unreadCount', unreadMessageCount, observablePayload);
    }

    if (typingEvents !== previousTypingEvents) {
        if (typingEvents.length) {
            const firstTypingEvent = _.first(typingEvents);
            observable.trigger('typing:start', {
                name: firstTypingEvent.name,
                avatarUrl: firstTypingEvent.avatarUrl,
                ...observablePayload,
            });
        } else {
            observable.trigger('typing:stop', observablePayload);
        }
    }

    if (triggerSound) {
        handleNotificationSound();
    }

    if (widgetState !== previousWidgetState || displayStyle !== previousDisplayStyle) {
        updateHostClassNames(widgetState, displayStyle);
    }

    initialStoreChange = false;
}

function cleanUp() {
    unmountComponentAtNode(document.querySelector('#mount'));
    disconnectFaye();
    stopMonitoringBrowserState();
    unsubscribeFromStore();
    storage.reset();
    store.dispatch(reset());
}

function checkInitialization() {
    const {
        appState: { isInitialized },
    } = store.getState();
    if (!isInitialized) {
        throw new Error('Must initialize the Web Messenger first using `init()`.');
    }
}

export function on(...args) {
    return observable.on(...args);
}

export function off(...args) {
    return observable.off(...args);
}

function intializeProps(props) {
    const newProps = {
        menuItems: {
            imageUpload: true,
            fileUpload: true,
            shareLocation: true,
        },
        imageUploadEnabled: true,
        soundNotificationEnabled: true,
        notificationChannelPromptEnabled: true,
        locale: DEFAULT_LOCALE,
        ...props,
    };

    newProps.menuItems.imageUpload = !!(
        newProps.menuItems.imageUpload &&
        newProps.imageUploadEnabled &&
        isFileUploadSupported()
    );
    newProps.menuItems.fileUpload = !!(
        newProps.menuItems.fileUpload &&
        newProps.imageUploadEnabled &&
        isFileUploadSupported()
    );

    return newProps;
}

function getCurrentUserIfAny(integrationId) {
    let sessionToken = storage.getItem(`${integrationId}.sessionToken`);
    let appUserId = storage.getItem(`${integrationId}.appUserId`);
    const clientId = storage.getItem(`${integrationId}.clientId`);

    if (sessionToken && appUserId && !clientId) {
        storage.removeItems(integrationId, ['sessionToken', 'appUserId']);
        sessionToken = null;
        appUserId = null;
    }

    return { sessionToken, appUserId };
}

function getConfigBaseUrl(props) {
    const region = props.region;
    const [, id] = resolveAppAndIntegrationParams(props);
    if (props.configBaseUrl) {
        return props.configBaseUrl;
    }

    if (region) {
        return `https://${id}.config.${region}.smooch.io/sdk/`;
    }

    return `https://${id}.config.smooch.io/sdk/`;
}

function resolveAppAndIntegrationParams({ appId, integrationId }) {
    if (integrationId) {
        return ['integrationId', integrationId];
    }

    return ['appId', appId];
}

export function init(props = {}) {
    const {
        appState: { isInitialized, widgetState },
    } = store.getState();
    if (isInitialized) {
        return Promise.reject(
            new Error('Web Messenger is already initialized. Call `destroy()` first before calling `init()` again.'),
        );
    }

    if (!props.appId && !props.integrationId) {
        return Promise.reject(new Error('Must provide an appId or an integrationId.'));
    }

    if (props.userId && props.authCode) {
        return Promise.reject(new Error('userId and authCode are mutually exclusive'));
    }

    lastTriggeredMessageTimestamp = 0;
    initialStoreChange = true;

    props = intializeProps(props);

    if (Object.values(DISPLAY_STYLE).includes(props.displayStyle)) {
        updateHostClassNames(widgetState, props.displayStyle);
    }

    storage.init({
        type: props.browserStorage || 'localStorage',
    });

    const { conversationColor, actionColor, brandColor } = props.customColors || {};

    const configBaseUrl = getConfigBaseUrl(props);
    if (props.delegate) {
        delegate.setDelegate(props.delegate);
    }

    const actions = [
        appStateActions.setEmbedded(!!props.embedded),
        setCurrentLocation(parent.document.location),
        setConfig(...resolveAppAndIntegrationParams(props)),
        setConfig('imageUploadEnabled', props.imageUploadEnabled && isFileUploadSupported()),
        setConfig('menuItems', props.menuItems),
        setConfig('soundNotificationEnabled', props.soundNotificationEnabled && isAudioSupported()),
        setConfig('configBaseUrl', configBaseUrl),
        setLocalSettings({
            accentColor: conversationColor,
            linkColor: actionColor,
            brandColor,
            ...pick(props, [
                'displayStyle',
                'buttonIconUrl',
                'buttonWidth',
                'buttonHeight',
                'businessName',
                'businessIconUrl',
                'integrationOrder',
                'backgroundImageUrl',
                'fixedIntroPane',
                'prechatCapture',
            ]),
        }),
        setConnectNotificationAllowed(props.notificationChannelPromptEnabled),
    ];

    if (props.customText) {
        actions.push(updateText(props.customText));
    }

    store.dispatch(batchActions(actions));
    unsubscribeFromStore = observeStore(
        store,
        ({
            config: { integrationId, displayStyle },
            conversations,
            appState: { widgetState, isInitialized, typingEvents, activeConversationId },
            browser: { currentLocation },
            user: { _id: currentUserId },
        }) => {
            return {
                isInitialized,
                conversation: conversations[activeConversationId],
                widgetState,
                displayStyle,
                currentLocation,
                integrationId,
                typingEvents,
                currentUserId,
            };
        },
        onStoreChange,
    );

    monitorBrowserState(store.dispatch.bind(store));

    let appUserId, sessionToken;
    return store
        .dispatch(fetchConfig())
        .then(() => {
            const { errorLoggingEnabled, integrationId } = store.getState().config;
            rollbarLib.init({
                accessToken: ROLLBAR_CLIENT_TOKEN,
                endpoint: `${ROLLBAR_ENDPOINT}item/`,
                environment: ROLLBAR_ENV,
                errorLoggingEnabled,
                integrationId,
                nodeEnv: process.env.NODE_ENV,
            });

            const user = getCurrentUserIfAny(integrationId);
            appUserId = user.appUserId;
            sessionToken = user.sessionToken;

            const dispatchUserActions = [];
            if (appUserId) {
                dispatchUserActions.push(
                    userActions.setUser({
                        _id: appUserId,
                    }),
                );
            }

            if (sessionToken) {
                dispatchUserActions.push(
                    authActions.setAuth({
                        sessionToken,
                    }),
                );
            }

            store.dispatch(batchActions(dispatchUserActions));
            return store.dispatch(setLocale(props.locale));
        })
        .then(() => {
            const {
                config: { isAppActive },
            } = store.getState();

            if (!isAppActive) {
                throw new Error('App is inactive.');
            }

            return Promise.resolve()
                .then(() => {
                    if (props.authCode) {
                        return store.dispatch(authActions.consumeAuthCode(props.authCode));
                    }
                })
                .then((user) => {
                    if (user) {
                        const { integrationId } = store.getState().config;
                        appUserId = user._id;
                        sessionToken = user.sessionToken;

                        storage.setItem(`${integrationId}.sessionToken`, sessionToken);
                        store.dispatch(
                            batchActions([
                                userActions.setUser({
                                    _id: user._id,
                                }),
                                authActions.setAuth({
                                    sessionToken: user.sessionToken,
                                }),
                            ]),
                        );
                    }
                })
                .then(() => {
                    if (props.userId && props.jwt) {
                        return store.dispatch(authActions.login(props.userId, props.jwt));
                    } else if (props.userId || props.jwt) {
                        throw new Error('Must provide a userId and a JWT in order to use the authenticated mode.');
                    }

                    if (appUserId && sessionToken) {
                        return store.dispatch(conversationActions.fetchUserConversation()).catch((err) => {
                            const code = err.response && err.response.status;
                            const message = err.message;
                            if ([401, 403, 404].includes(code) || message === CLIENT_ID_ERROR) {
                                storage.removeItems(props.integrationId, ['appUserId', 'sessionToken']);

                                return store.dispatch(batchActions([authActions.resetAuth(), userActions.resetUser()]));
                            } else {
                                throw err;
                            }
                        });
                    }
                })
                .then(() => {
                    renderWidget();
                    store.dispatch(appStateActions.setInitializationState(true));
                    observable.trigger('ready');
                });
        })
        .catch((err) => {
            rollbarLib.client() && rollbarLib.client().error(err);
            cleanUp();
            throw err;
        });
}

export function login(userId, jwt) {
    return Promise.resolve()
        .then(() => checkInitialization())
        .then(() => {
            if (!userId || !jwt) {
                return Promise.reject(new Error('Must provide a userId and a JWT to log in.'));
            }

            return store.dispatch(authActions.login(userId, jwt));
        });
}

export function logout() {
    return Promise.resolve()
        .then(() => checkInitialization())
        .then(() => store.dispatch(authActions.logout()));
}

export function sendMessage(props, conversationId) {
    checkInitialization();

    if (conversationId && !isValidConversationId(conversationId)) {
        return Promise.reject(new Error('Invalid conversationId'));
    }

    const {
        appState: { activeConversationId },
    } = store.getState();

    if (!conversationId || conversationId === activeConversationId) {
        // should fall back to currently viewed conversation
        return store.dispatch(conversationActions.sendMessage(props));
    }

    return store
        .dispatch(conversationActions.fetchConversationIfNeeded(conversationId))
        .then(() => store.dispatch(conversationActions.sendMessage(props, { conversationId })));
}

export function triggerPostback(actionId, conversationId) {
    checkInitialization();

    if (conversationId && !isValidConversationId(conversationId)) {
        return Promise.reject(new Error('Invalid conversationId'));
    }

    const {
        appState: { activeConversationId },
    } = store.getState();

    if (!conversationId || conversationId === activeConversationId) {
        // should fall back to currently viewed conversation
        return store.dispatch(conversationActions.sendPostback(actionId));
    }

    return store
        .dispatch(conversationActions.fetchConversationIfNeeded(conversationId))
        .then(() => store.dispatch(conversationActions.sendPostback(actionId, conversationId)));
}

export function updateUser(props) {
    if (props && props.email !== undefined && typeof props.email !== 'string') {
        return Promise.reject('Email address must be a string.');
    }

    return Promise.resolve()
        .then(() => checkInitialization())
        .then(() => store.dispatch(userActions.update(props)));
}

export function getConversation() {
    checkInitialization();

    const {
        conversations,
        appState: { activeConversationId },
    } = store.getState();
    const conversation = conversations[activeConversationId];
    conversation.messages = conversation.messages.map((message) => filterClientProps(message));
    return formatConversationOnGet(conversation);
}

export function getConversationById(conversationId) {
    checkInitialization();

    if (conversationId && typeof conversationId !== 'string') {
        return Promise.reject(new Error('Invalid conversationId'));
    }

    const {
        conversations,
        appState: { activeConversationId },
    } = store.getState();

    conversationId = conversationId || activeConversationId;

    // conversation in the conversations reducer
    if (conversations[conversationId]) {
        const conversation = conversations[conversationId];
        if (conversation.messagesUpdated) {
            conversation.messages = conversation.messages.map((message) => filterClientProps(message));
            return Promise.resolve(formatConversationOnGet(conversation));
        }
    }

    return store.dispatch(conversationActions.getConversation(conversationId)).then(() => {
        const { conversations } = store.getState();
        const conversation = conversations[conversationId];

        if (!conversation) {
            return Promise.reject(new Error('Conversation not found'));
        }

        conversation.messages = conversation.messages.map((message) => filterClientProps(message));

        return formatConversationOnGet(conversation);
    });
}

export function getConversations(offset = 0) {
    checkInitialization();

    return store.dispatch(conversationActions.getConversations(offset)).then((conversations) => {
        return Object.values(conversations)
            .sort((conversationA, conversationB) => conversationB.lastUpdatedAt - conversationA.lastUpdatedAt)
            .slice(offset, offset + 10)
            .map((conversation) => {
                conversation.messages = conversation.messages.map((message) => filterClientProps(message));
                return formatConversationOnGet(conversation);
            });
    });
}

export function getUser() {
    const { user, pendingUserProps } = store.getState();

    if (Object.keys(user).length === 0 && Object.keys(pendingUserProps).length === 0) {
        return undefined;
    }

    return Object.assign({}, user, pendingUserProps, {
        properties: Object.assign({}, user.properties, pendingUserProps.properties),
    });
}

export function destroy() {
    // `destroy()` only need to clean up handlers
    // the rest will be cleaned up with the iframe removal
    const {
        appState: { isInitialized },
    } = store.getState();
    if (!isInitialized) {
        return;
    }
    cleanUp();
    observable.trigger('destroy');
    observable.off();
}

export function open() {
    checkInitialization();
    store.dispatch(appStateActions.openWidget());
}

export function close() {
    checkInitialization();
    store.dispatch(appStateActions.closeWidget());
}

export function isOpened() {
    return store.getState().appState.widgetState === WIDGET_STATE.OPENED;
}

export function render() {
    checkInitialization();
    return renderWidget();
}

export function loadConversation(conversationId) {
    return store.dispatch(conversationActions.loadConversation(conversationId));
}

export function startConversation() {
    return store.dispatch(conversationActions.startConversation('conversation:start'));
}

export function markAllAsRead(conversationId) {
    store.dispatch(conversationActions.resetUnreadCount(conversationId));
}

export function showNotificationChannelPrompt() {
    store.dispatch(conversationActions.handleConnectNotification({ force: true }));
}

export function setPredefinedMessage(message) {
    store.dispatch(updateText({ startingText: (message && message.trim()) || '' }));
}

export function setDelegate(delegateOptions) {
    delegate.setDelegate(delegateOptions);
}

export function startTyping(conversationId) {
    return Promise.resolve(checkInitialization()).then(() => store.dispatch(typingActions.startTyping(conversationId)));
}

export function stopTyping(conversationId) {
    return Promise.resolve(checkInitialization()).then(() => store.dispatch(typingActions.stopTyping(conversationId)));
}
